import { useCallback, useEffect, useRef } from "react";

import * as Sentry from "@sentry/react";
import type { StoreApi } from "zustand/vanilla";

import { withManagedTemplateGeometry } from "./deskEvaluationHydration";
import { useOptionalWorkspaceRepository } from "./WorkspaceRepository.context";
import {
  EMPTY_WORKSPACE_SNAPSHOT,
  type WorkspaceDocument,
} from "./workspace.document";
import type { WorkspaceCommitResult } from "./workspace.repository";
import type { PendingWorkspaceSnapshot } from "./workspace.storage";
import type { WorkspaceSnapshot, WorkspaceStore } from "./workspace.types";

const fingerprintWorkspace = (snapshot: WorkspaceSnapshot): string =>
  JSON.stringify({
    engine: snapshot.engine,
    widgets: [...snapshot.widgets].sort((a, b) => a.id.localeCompare(b.id)),
    placements: [...snapshot.placements].sort((a, b) =>
      a.widgetId.localeCompare(b.widgetId),
    ),
    widgetSettings: Object.fromEntries(
      Object.entries(snapshot.widgetSettings).sort(([left], [right]) =>
        left.localeCompare(right),
      ),
    ),
  });

interface LayoutDocumentPersistenceOptions {
  enabled: boolean;
  workspaceId: string | null;
  store: StoreApi<WorkspaceStore>;
  settings: Record<string, unknown>;
  onHydrateSettings?: (settings: Record<string, unknown>) => void;
}

const reportWorkspacePersistenceError = (error: unknown) => {
  Sentry.captureException(error, {
    tags: { component: "workspace-persistence" },
  });
};

export const useLayoutDocumentPersistence = ({
  enabled,
  workspaceId,
  store,
  settings,
  onHydrateSettings,
}: LayoutDocumentPersistenceOptions): { clearPersisted: () => void } => {
  const repository = useOptionalWorkspaceRepository();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const pendingRef = useRef<{
    baseRevision: number;
    snapshot: PendingWorkspaceSnapshot;
    workspaceFingerprint: string;
    settingsFingerprint: string;
  }>();
  const hydratingRef = useRef(false);
  const skipSettingsEffectRef = useRef(false);
  const renderedSettingsFingerprintRef = useRef(JSON.stringify(settings));
  const initialEntry =
    repository && workspaceId
      ? repository.document.workspaces.find((entry) => entry.id === workspaceId)
      : undefined;
  const workspaceFingerprintRef = useRef(
    initialEntry ? fingerprintWorkspace(initialEntry.workspace) : undefined,
  );
  const settingsFingerprintRef = useRef(
    initialEntry ? JSON.stringify(initialEntry.settings) : undefined,
  );

  const hydrateDocument = useCallback(
    (document: WorkspaceDocument) => {
      if (!workspaceId) {
        return;
      }
      const entry = document.workspaces.find(
        (workspace) => workspace.id === workspaceId,
      );
      if (!entry) {
        return;
      }
      const workspaceFingerprint = fingerprintWorkspace(entry.workspace);
      const settingsFingerprint = JSON.stringify(entry.settings);
      const workspaceChanged =
        workspaceFingerprint !== workspaceFingerprintRef.current;
      const settingsChanged =
        settingsFingerprint !== settingsFingerprintRef.current;
      if (!(workspaceChanged || settingsChanged)) {
        return;
      }
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      hydratingRef.current = true;
      if (workspaceChanged) {
        workspaceFingerprintRef.current = workspaceFingerprint;
        const currentSnapshot = store.getState().toSnapshot();
        if (
          fingerprintWorkspace(currentSnapshot) !==
          fingerprintWorkspace(entry.workspace)
        ) {
          store
            .getState()
            .applySnapshot(
              withManagedTemplateGeometry(entry.workspace, entry.templateId),
            );
        }
      }
      if (settingsChanged) {
        settingsFingerprintRef.current = settingsFingerprint;
        if (onHydrateSettings) {
          skipSettingsEffectRef.current = true;
          onHydrateSettings(entry.settings);
        }
      }
      hydratingRef.current = false;
    },
    [onHydrateSettings, store, workspaceId],
  );

  const adoptPendingCommit = useCallback(
    (result: WorkspaceCommitResult) => {
      const pending = pendingRef.current;
      if (!pending || !repository || !workspaceId) {
        return;
      }
      if (!result.consumedMutationIds.includes(pending.snapshot.mutationId)) {
        if (
          repository.getPendingWorkspaceMutationStatus(
            workspaceId,
            pending.snapshot.mutationId,
          ) !== "superseded"
        ) {
          return;
        }
        pendingRef.current = undefined;
        if (timerRef.current !== undefined) {
          clearTimeout(timerRef.current);
          timerRef.current = undefined;
        }
        hydrateDocument(result.document);
        return;
      }
      const entry = result.document.workspaces.find(
        (workspace) => workspace.id === workspaceId,
      );
      workspaceFingerprintRef.current = entry
        ? fingerprintWorkspace(entry.workspace)
        : undefined;
      settingsFingerprintRef.current = entry
        ? JSON.stringify(entry.settings)
        : undefined;
      pendingRef.current = undefined;
    },
    [hydrateDocument, repository, workspaceId],
  );

  const stage = useCallback(() => {
    if (!(enabled && workspaceId && repository)) {
      return false;
    }
    const snapshot = store.getState().toSnapshot();
    const nextSettings = structuredClone(settingsRef.current);
    const workspaceFingerprint = fingerprintWorkspace(snapshot);
    const settingsFingerprint = JSON.stringify(nextSettings);
    if (
      workspaceFingerprint === workspaceFingerprintRef.current &&
      settingsFingerprint === settingsFingerprintRef.current
    ) {
      return false;
    }
    const staged = repository.stageWorkspaceSnapshot(
      workspaceId,
      snapshot,
      nextSettings,
    );
    if (!staged) {
      return false;
    }
    pendingRef.current = {
      baseRevision: repository.getSnapshot().revision,
      snapshot: staged,
      workspaceFingerprint,
      settingsFingerprint,
    };
    return true;
  }, [enabled, repository, store, workspaceId]);

  const flush = useCallback(() => {
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    if (!(pendingRef.current && enabled && workspaceId && repository)) {
      return;
    }
    void repository
      .recoverPendingWorkspaces()
      .then(adoptPendingCommit)
      .catch(reportWorkspacePersistenceError);
  }, [adoptPendingCommit, enabled, repository, workspaceId]);

  const schedule = useCallback(() => {
    if (hydratingRef.current || !(enabled && workspaceId && repository)) {
      return;
    }
    if (!stage()) {
      return;
    }
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(flush, 0);
  }, [enabled, flush, repository, stage, workspaceId]);

  const scheduleWorkspace = useCallback(() => {
    if (hydratingRef.current || !(enabled && workspaceId && repository)) {
      return;
    }
    const workspaceFingerprint = fingerprintWorkspace(
      store.getState().toSnapshot(),
    );
    const expectedFingerprint =
      pendingRef.current?.workspaceFingerprint ??
      workspaceFingerprintRef.current;
    if (workspaceFingerprint === expectedFingerprint) {
      return;
    }
    schedule();
  }, [enabled, repository, schedule, store, workspaceId]);

  const acknowledgePendingNotification = useCallback(
    (workspaceFingerprint: string, settingsFingerprint: string): boolean => {
      const pending = pendingRef.current;
      if (!pending) {
        return false;
      }
      if (
        repository &&
        repository.getSnapshot().revision > pending.baseRevision &&
        workspaceFingerprint === pending.workspaceFingerprint &&
        settingsFingerprint === pending.settingsFingerprint
      ) {
        workspaceFingerprintRef.current = workspaceFingerprint;
        settingsFingerprintRef.current = settingsFingerprint;
        pendingRef.current = undefined;
        if (timerRef.current !== undefined) {
          clearTimeout(timerRef.current);
          timerRef.current = undefined;
        }
      }
      return true;
    },
    [repository],
  );

  const reconcileRepositorySnapshot = useCallback(() => {
    if (!repository || !workspaceId) {
      return;
    }
    const document = repository.getSnapshot();
    const entry = document.workspaces.find((item) => item.id === workspaceId);
    if (!entry) {
      return;
    }
    const workspaceFingerprint = fingerprintWorkspace(entry.workspace);
    const settingsFingerprint = JSON.stringify(entry.settings);
    if (
      acknowledgePendingNotification(workspaceFingerprint, settingsFingerprint)
    ) {
      return;
    }
    hydrateDocument(document);
  }, [
    acknowledgePendingNotification,
    hydrateDocument,
    repository,
    workspaceId,
  ]);

  useEffect(() => {
    if (!(enabled && repository && workspaceId)) {
      return;
    }
    return store.subscribe(scheduleWorkspace);
  }, [enabled, repository, scheduleWorkspace, store, workspaceId]);

  useEffect(() => {
    if (!(enabled && repository && workspaceId)) {
      return;
    }
    return repository.subscribe(reconcileRepositorySnapshot);
  }, [enabled, reconcileRepositorySnapshot, repository, workspaceId]);

  useEffect(() => {
    const clearPending = () => {
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      pendingRef.current = undefined;
    };
    const preservePendingOnPageHide = () => {
      clearPending();
    };
    const recoverOnVisibilityChange = () => {
      if (enabled && repository) {
        void repository
          .recoverPendingWorkspaces()
          .then(adoptPendingCommit)
          .catch(reportWorkspacePersistenceError);
      }
    };
    window.addEventListener("pagehide", preservePendingOnPageHide);
    document.addEventListener("visibilitychange", recoverOnVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", preservePendingOnPageHide);
      document.removeEventListener(
        "visibilitychange",
        recoverOnVisibilityChange,
      );
      clearPending();
    };
  }, [adoptPendingCommit, enabled, repository]);

  useEffect(() => {
    const renderedFingerprint = JSON.stringify(settings);
    if (skipSettingsEffectRef.current) {
      skipSettingsEffectRef.current = false;
      renderedSettingsFingerprintRef.current = renderedFingerprint;
      return;
    }
    if (renderedFingerprint === renderedSettingsFingerprintRef.current) {
      return;
    }
    renderedSettingsFingerprintRef.current = renderedFingerprint;
    schedule();
  }, [schedule, settings]);

  return {
    clearPersisted: () => {
      if (!(enabled && workspaceId && repository)) {
        return;
      }
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      pendingRef.current = undefined;
      void repository
        .mutateWorkspace(workspaceId, () => ({
          workspace: EMPTY_WORKSPACE_SNAPSHOT,
          settings: {},
        }))
        .catch(reportWorkspacePersistenceError);
    },
  };
};
